import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthRoutingService {
  private readonly RETURN_URL_KEY = 'auth_return_url';

  constructor() {}

  /**
   * Get the base URL for the portal app based on environment
   */
  getPortalUrl(): string {
    return environment.appUrls.portal;
  }

  /**
   * Get the base URL for the website app based on environment
   */
  getWebsiteUrl(): string {
    return environment.appUrls.website;
  }

  /**
   * Store the intended return URL before redirecting to login
   */
  setReturnUrl(url: string): void {
    sessionStorage.setItem(this.RETURN_URL_KEY, url);
  }

  /**
   * Get the stored return URL and clear it from storage
   */
  getReturnUrl(): string | null {
    const returnUrl = sessionStorage.getItem(this.RETURN_URL_KEY);
    if (returnUrl) {
      sessionStorage.removeItem(this.RETURN_URL_KEY);
    }
    return returnUrl;
  }

  /**
   * Redirect to the portal app with optional return URL.
   * Uses relative navigation when already on the portal to avoid hardcoded port issues.
   */
  redirectToPortal(returnUrl?: string): void {
    const path = returnUrl || '/care';

    if (this.isOnPortal()) {
      // Already on the portal — navigate relatively (works on any port)
      window.location.href = path;
    } else {
      // Cross-app redirect (e.g. website → portal)
      window.location.href = `${this.getPortalUrl()}${path}`;
    }
  }

  /**
   * Redirect to the website login with optional return URL
   */
  redirectToLogin(returnUrl?: string): void {
    if (returnUrl) {
      this.setReturnUrl(returnUrl);
    }

    const websiteUrl = this.getWebsiteUrl();
    window.location.href = `${websiteUrl}/auth/login`;
  }

  /**
   * Redirect to website home page
   */
  redirectToWebsite(): void {
    const websiteUrl = this.getWebsiteUrl();
    window.location.href = websiteUrl;
  }

  /**
   * Check if we're currently running on the portal origin.
   * In production different apps have different hostnames, so origin comparison works.
   * On localhost all apps share the same hostname — fall back to exact origin match.
   */
  isPortalApp(): boolean {
    return this.isOnPortal();
  }

  /**
   * Check if current app is the website
   */
  isWebsiteApp(): boolean {
    return window.location.origin === this.getWebsiteUrl();
  }

  /**
   * Detect whether the current page is the portal app.
   * In production: compare full origin (different hostnames).
   * On localhost: compare full origin (different ports distinguish apps).
   * As a fallback for CI/dynamic ports: if origins don't match any known app,
   * assume we're on the portal (the only app that calls portal redirects internally).
   */
  private isOnPortal(): boolean {
    const currentOrigin = window.location.origin;
    const portalOrigin = new URL(this.getPortalUrl()).origin;
    const websiteOrigin = new URL(this.getWebsiteUrl()).origin;

    // Exact match — definitely on the portal
    if (currentOrigin === portalOrigin) {
      return true;
    }

    // If we match the website origin, we're NOT on the portal
    if (currentOrigin === websiteOrigin) {
      return false;
    }

    // Unknown origin (e.g. CI with different port) — if we're not the website,
    // we must be the portal
    return true;
  }

  /**
   * Get current app context
   */
  getCurrentAppContext(): 'portal' | 'website' {
    return this.isPortalApp() ? 'portal' : 'website';
  }

  /**
   * Handle successful login - redirect to portal
   */
  handleLoginSuccess(): void {
    const returnUrl = this.getReturnUrl();
    this.redirectToPortal(returnUrl || '/care');
  }

  /**
   * Handle logout - redirect to website home
   */
  handleLogout(): void {
    // Clear any stored return URLs
    sessionStorage.removeItem(this.RETURN_URL_KEY);
    this.redirectToWebsite();
  }
}
